// The tick contract: runs all systems in a FIXED order. Changing the order changes results.
// AI runs right after applyCommands and emits commands that are queued for the NEXT tick.
import type { SimServices } from './context';
import type { StepContext } from './context';
import type { GameState, Command, GameEvent } from './types';
import { applyCommands } from './systems/apply-commands';
import { productionSystem } from './systems/production';
import { movementSystem } from './systems/movement';
import { combatSystem } from './systems/combat';
import { garrisonSystem } from './systems/garrison';
import { projectileSystem } from './systems/projectile';
import { auraSystem } from './systems/aura';
import { harvestSystem } from './systems/harvest';
import { channelSystem } from './systems/channel';
import { deathSystem } from './systems/death';
import { morphSystem } from './systems/morph';
import { visibilitySystem } from './systems/visibility';
import { winCheckSystem } from './systems/wincheck';
import { superweaponSystem } from './systems/superweapon';
import { beamWeaponSystem } from './systems/beam-weapon';
import { applySandboxEconomyCheats } from './systems/sandbox-economy';

export type AiHook = (state: GameState, services: SimServices) => Command[];

export interface StepResult {
  events: GameEvent[];
  nextCommands: Command[];
}

export function stepSimulation(
  state: GameState,
  services: SimServices,
  cmds: Command[],
  aiHook?: AiHook,
): StepResult {
  const ctx: StepContext = { services, events: [] };
  const nextCommands: Command[] = [];

  services.spatial.rebuild(state.entities);

  applyCommands(state, ctx, cmds); // 1
  if (aiHook && !state.ended) nextCommands.push(...aiHook(state, services)); // 2 AI
  productionSystem(state, ctx); // 3
  morphSystem(state, ctx); // 3b mobile HQ deploy/pack
  movementSystem(state, ctx); // 4/5 (pathing computed on demand inside)
  visibilitySystem(state, ctx); // 5b fog of war
  combatSystem(state, ctx); // 6
  beamWeaponSystem(state, ctx); // 6a continuous tower beams
  garrisonSystem(state, ctx); // 6b garrisoned units fire from bunkers
  projectileSystem(state, ctx); // 6c projectiles
  superweaponSystem(state, ctx); // 6d superweapon beams
  auraSystem(state, ctx); // 6d passive support auras
  harvestSystem(state, ctx); // 7
  channelSystem(state, ctx); // 7b mana weaver conjuring
  deathSystem(state, ctx); // 8
  winCheckSystem(state, ctx); // 9
  applySandboxEconomyCheats(state, ctx); // 9b sandbox mana/power cheats

  state.tick++;
  return { events: ctx.events, nextCommands };
}
